import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Representative } from '../../model/representative';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Subscription, finalize, of, switchMap } from 'rxjs';
import { RepresentativeService } from '../../service/data/representative.service';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { ENGLISH_CULTURE, LanguageService, SWEDISH_CULTURE } from '../../../core/service/language.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { TenantCategory, TenantCategoryToString, TenantState } from '../../model/const/enums';
import { MessageService } from '../../../core/service/message.service';
import { ContactService } from '../../service/data/contact.service';
import { ContactIdKind } from '../../model/contact';
import { IdType } from '../../model/id-type';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { RentalObjectKeyHandover, RentalObjectKeyHandoverDirection } from '../../model/rental-object-key';
import { RentalObjectKeyHandoverService } from '../../service/data/rental-object-key-handover.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { UserDialogService } from '../../../core/service/user-dialog.service';

@Component({
    selector: 'app-rental-object-key-handover-form',
    templateUrl: './rental-object-key-handover-form.component.html',
    styleUrls: ['./rental-object-key-handover-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentalObjectKeyHandoverFormComponent implements OnInit, OnDestroy {
  constructor(
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory,
    private _actionService: ActionWindowService,
    private _navigationService: NavigationService,
    private _messageService: MessageService,
    private _dialogService: UserDialogService,
    private _dataService: RentalObjectKeyHandoverService
  ) {}

  form: FormGroup;
  private _sub: Subscription = new Subscription();

  @Input() dataContext: {
    create: boolean;
    rentalObjectKeyId: string;
    keysInStore: number;
    totalKeys: number;
    keysLentOut: number;
    handover: RentalObjectKeyHandover;
  };
  @Output() cancelOperation = new EventEmitter<void>();
  @Output() deleteOperation = new EventEmitter<void>();
  @Output() formSubmitted = new EventEmitter<void>();

  ngOnInit(): void {
    this.form = this._fb.group({
      id: this._fcf.createHidden(this.dataContext.handover.id),
      direction: this._fcf.createHidden(this.dataContext.handover.direction),
      quantity: this._fcf.create('COMMON.QUANTITY', this.dataContext.handover.quantity),
      comment: this._fcf.create('COMMON.NOTE', this.dataContext.handover.comment),
      remainingInKeyCabinet: this._fcf.createReadonly('RENTALOBJECT_KEY.QUANTITY_IN_STORAGE', this.dataContext.keysInStore),
    });

    let qControl = this.form.controls['quantity'];
    this._sub.add(qControl.valueChanges.subscribe(x => this.updateStorage()));

    if (this.dataContext?.handover.direction === RentalObjectKeyHandoverDirection.ToLandlord) {
      if (this.dataContext.handover.isNew) {
        qControl.setValue(this.dataContext.keysLentOut);
        qControl.setValidators([Validators.min(1), Validators.max(this.dataContext.keysLentOut)]);
      } else {
        qControl.setValidators([Validators.min(1), Validators.max(this.dataContext.handover.quantity + this.dataContext.keysLentOut)]);
      }
    } else {
      if (this.dataContext.handover.isNew) {
        qControl.setValue(this.dataContext.keysInStore);
        qControl.setValidators([Validators.min(1), Validators.max(this.dataContext.keysInStore)]);
      } else {
        qControl.setValidators([Validators.min(1), Validators.max(this.dataContext.handover.quantity + this.dataContext.keysInStore)]);
      }
    }
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    if (this.deleteSub) this.deleteSub.unsubscribe();
  }

  get header(): string {
    if (this.dataContext.handover.isNew)
      return this.dataContext.handover.direction == RentalObjectKeyHandoverDirection.ToTenant
        ? 'RENTALOBJECT_KEY.REGISTER_HANDOUT'
        : 'RENTALOBJECT_KEY.REGISTER_RETURN';
    return this.dataContext.handover.direction == RentalObjectKeyHandoverDirection.ToTenant
      ? 'RENTALOBJECT_KEY.EDIT_HANDOUT'
      : 'RENTALOBJECT_KEY.EDIT_RETURN';
  }

  get infoText(): string {
    return this.dataContext.handover.direction == RentalObjectKeyHandoverDirection.ToTenant && this.dataContext.handover.isNew
      ? 'RENTALOBJECT_KEY.CREATE_WITH_ESIGN_DIRECTION'
      : '';
  }

  updateStorage(): void {
    let quantity = this.form.controls['quantity'].value;
    let storageControl = this.form.controls['remainingInKeyCabinet'];

    if (this.dataContext.handover.isNew) {
      if (this.dataContext.handover.direction == RentalObjectKeyHandoverDirection.ToLandlord) {
        storageControl.setValue(this.dataContext.keysInStore + quantity);
      } else {
        storageControl.setValue(this.dataContext.keysInStore - quantity);
      }
      return;
    }

    let change = quantity - this.dataContext.handover.quantity;
    if (this.dataContext.handover.direction == RentalObjectKeyHandoverDirection.ToLandlord) {
      storageControl.setValue(this.dataContext.keysInStore + change);
    } else {
      storageControl.setValue(this.dataContext.keysInStore - change);
    }
  }
  submitButtonDisabled: boolean;
  onSubmit() {
    if (this.form.pristine && !this.dataContext.handover.isNew) {
      return;
    }

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitButtonDisabled = true;

    const completeAction = (value: RentalObjectKeyHandover) => {
      this.dataContext.keysInStore = this.form.controls['remainingInKeyCabinet'].value;
      this.dataContext.keysLentOut = this.dataContext.totalKeys - this.dataContext.keysInStore;

      this._actionService.complete(value);
      this.formSubmitted.emit();
    };

    let model = new RentalObjectKeyHandover();
    Object.assign(model, this.form.value);
    model['rentalObjectKeyId'] = this.dataContext.rentalObjectKeyId;

    if (this.dataContext.handover.isNew) {
      this._sub.add(
        this._dataService
          .add(model)
          .pipe(
            finalize(() => {
              this.submitButtonDisabled = false;
            })
          )
          .subscribe(res => {
            this.setModelValuesFromForm(this.dataContext.handover);
            this.dataContext.handover.id = res;
            completeAction(model);
          })
      );
    } else {
      this._sub.add(
        this._dataService
          .change(model)
          .pipe(
            finalize(() => {
              this.submitButtonDisabled = false;
            })
          )
          .subscribe(() => {
            this.setModelValuesFromForm(this.dataContext.handover);
            completeAction(model);
          })
      );
    }
  }

  private setModelValuesFromForm(model: RentalObjectKeyHandover) {
    model.comment = this.form.controls['comment'].value;
    model.directionCaption =
      model.direction == RentalObjectKeyHandoverDirection.ToLandlord ? 'RENTALOBJECT_KEY.RETURN' : 'RENTALOBJECT_KEY.HANDOUT';
    model.quantity = this.form.controls['quantity'].value;
  }

  onFormCancel() {
    this.cancelOperation.emit();
  }

  private deleteSub: Subscription;
  onFormDelete() {
    if (this.submitButtonDisabled) return;

    if (this.deleteSub) {
      this.deleteSub.unsubscribe();
    }
    this.deleteSub = this._dialogService.show('DO_YOU_WANT_TO_DELETE_RECORD').subscribe(x => {
      if (x) {
        this.submitButtonDisabled = true;

        this._sub.add(
          this._dataService
            .delete(this.dataContext.handover.id)
            .pipe(
              finalize(() => {
                this.submitButtonDisabled = false;
              })
            )
            .subscribe(res => {
              this.deleteOperation.emit();
            })
        );
      }
    });
  }

  get showEsignatureLink(): boolean {
    return (this.dataContext.handover.eSignatureErrandId ?? '').length > 0;
  }

  openEsignature(event: MouseEvent) {
    this._navigationService.navigate(['esignature-errand', 'edit', this.dataContext.handover.eSignatureErrandId], null, event.ctrlKey);
    this.cancelOperation.emit();
  }
}
